/**
 * The permission categories a mailbox grant carries and the agent email tools
 * each one admits. A mailbox grant has no provider catalog behind it, so this
 * map is its catalog: the API validates grants against it, the agent runtime
 * resolves tools through it, and the grant dialog renders it.
 */
export const MAILBOX_GRANT_TOOL_MAP = {
  read: ["list_mailboxes", "list_emails", "get_email"],
  send: ["send_email", "check_sent_email_status"],
} as const;

export type MailboxGrantCategory = keyof typeof MAILBOX_GRANT_TOOL_MAP;
export type MailboxToolId = (typeof MAILBOX_GRANT_TOOL_MAP)[MailboxGrantCategory][number];

export const MAILBOX_GRANT_CATEGORIES = Object.keys(
  MAILBOX_GRANT_TOOL_MAP,
) as readonly MailboxGrantCategory[];

export const MAILBOX_TOOL_IDS = Object.values(
  MAILBOX_GRANT_TOOL_MAP,
).flat() as readonly MailboxToolId[];

export function isMailboxGrantCategory(value: string): value is MailboxGrantCategory {
  return Object.hasOwn(MAILBOX_GRANT_TOOL_MAP, value);
}

export function mailboxToolCategory(toolId: MailboxToolId): MailboxGrantCategory {
  return MAILBOX_GRANT_CATEGORIES.find((category) =>
    (MAILBOX_GRANT_TOOL_MAP[category] as readonly string[]).includes(toolId),
  )!;
}

/** Row labels for the grant dialog's per-tool toggles. */
export const MAILBOX_TOOL_INFO: Record<MailboxToolId, { name: string; description: string }> = {
  list_mailboxes: { name: "List Mailboxes", description: "List the mailboxes this agent may use" },
  list_emails: { name: "List Emails", description: "List this mailbox's emails" },
  get_email: { name: "Read Email", description: "Read one email's body and attachments" },
  send_email: { name: "Send Email", description: "Send or reply from this mailbox" },
  check_sent_email_status: {
    name: "Check Sent Email Status",
    description: "Check whether a sent email was delivered, held or failed",
  },
};
